import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import downloadCsv from "../../lib/downloadCsv";

export const SAMPLE_LEAD: Record<string, string> = {
    lead_id: "123456",
    email: "test@test.com",
    first_name: "John",
    last_name: "Doe",
    home_phone: "2025551212",
    address: "1600 Pennsylvania Ave NW",
    city: "Washington",
    state: "DC",
    zip: "20500",
    current_date: "",
    edu_level_id: "3",
    high_school_grad_year: "2001",
    military: "1",
    subject: "Business",
    url: "https://www.degreelocate.com",
    creative_url: "https://www.degreelocate.com/site/images/logo.png",
    subsource1: "abc",
    subsource2: "123",
};

const CHECKER = `
    on
    url
    body
    headers
    resolutions { type regex }
`;

const ROW_FIELDS = `
    portalRedirectId
    companyName
    brokerName
    schoolName
    appId
    hpCampaignId
    hpCampaignIdLb
    hpCampaignIdLbTemp
    landingPage
    previewUrl
    redirectType
    dailyCap
    monthlyCap
    ppi
    dailyLbMatchPercent
    activeFlag
    isBranded
    isExclusive
    isExclusiveTemp
    isExclusiveOnly
    autoPpi
    campaignGrade
    campaignGradeLb
    cplSf
    cplLb
    hpCampusSearchId
    hpCampusSearchIdLb
    hpEventIdSf
    hpEventIdLb
    isFclb
    hasActiveGroupCaps
    hasInactiveGroupCaps
    campaignActive
    impressions
    leads
    leadsLb
    leadsTotal
    revenue
    ctr
    cpi
    monthly
    monthlyLb
    monthlyTotal
    skip
    skipPercent
    whenStart
    brokerId
    schoolId
    tiers { A B C D E F G }
    dupeChecker { on }
    scoreChecker { on }
`;

export async function searchPortalRedirectCampaigns(input: any) {
    const q = gql`
        query q($input: PortalRedirectCampaignsInput!) {
            portalRedirectCampaigns(input: $input) { ${ROW_FIELDS} }
        }
    `;
    const res = await query(q, {input});
    if (!res) {
        return null;
    }
    return res.portalRedirectCampaigns || [];
}

export async function fetchPrCampaignCheckers(portalRedirectId: number) {
    const q = gql`
        query q($portalRedirectId: Int!) {
            portalRedirectCampaignCheckers(portalRedirectId: $portalRedirectId) {
                dupeChecker { ${CHECKER} }
                scoreChecker { ${CHECKER} }
            }
        }
    `;
    const res = await query(q, {portalRedirectId});
    return res?.portalRedirectCampaignCheckers || null;
}

export async function fetchPrComments(portalRedirectId: number) {
    const q = gql`
        query q($portalRedirectId: Int!) {
            portalRedirectCampaignComments(portalRedirectId: $portalRedirectId) {
                id repId date name comments
            }
        }
    `;
    const res = await query(q, {portalRedirectId});
    return res?.portalRedirectCampaignComments || [];
}

export async function fetchSkipStats(hpCampaignId: number, startDate: string, endDate: string) {
    const q = gql`
        query q($hpCampaignId: Int!, $startDate: String!, $endDate: String!) {
            portalRedirectDegreeSeekingStats(hp_campaign_id: $hpCampaignId, startDate: $startDate, endDate: $endDate) {
                degree impressions submissions skips
            }
        }
    `;
    const res = await query(q, {hpCampaignId, startDate, endDate});
    return res?.portalRedirectDegreeSeekingStats || [];
}

async function run(mutation: any, variables: any) {
    const res = await mutate(mutation, variables);
    const key = Object.keys(res || {}).find((k) => k.startsWith("portalRedirect"));
    return key ? !!res[key] : false;
}

export function upsertPrCampaign(input: any) {
    return run(gql`mutation m($input: PortalRedirectCampaignUpsertInput!) { portalRedirectCampaignUpsert(input: $input) }`, {input});
}

export function clonePrCampaign(input: any) {
    return run(gql`mutation m($input: PortalRedirectCampaignCloneInput!) { portalRedirectCampaignClone(input: $input) }`, {input});
}

export function setPrActive(portalRedirectId: number, active: boolean) {
    return run(gql`mutation m($portalRedirectId: Int!, $active: Boolean!) { portalRedirectCampaignSetActive(portalRedirectId: $portalRedirectId, active: $active) }`, {portalRedirectId, active});
}

export function zeroPrDailyCap(portalRedirectId: number) {
    return run(gql`mutation m($portalRedirectId: Int!) { portalRedirectCampaignZeroDailyCap(portalRedirectId: $portalRedirectId) }`, {portalRedirectId});
}

export function setPrExclusive(portalRedirectId: number, state: boolean) {
    return run(gql`mutation m($portalRedirectId: Int!, $state: Boolean!) { portalRedirectCampaignSetExclusive(portalRedirectId: $portalRedirectId, state: $state) }`, {portalRedirectId, state});
}

export function setPrExclusiveOnly(portalRedirectId: number, state: boolean) {
    return run(gql`mutation m($portalRedirectId: Int!, $state: Boolean!) { portalRedirectCampaignSetExclusiveOnly(portalRedirectId: $portalRedirectId, state: $state) }`, {portalRedirectId, state});
}

export function togglePrLb(portalRedirectId: number) {
    return run(gql`mutation m($portalRedirectId: Int!) { portalRedirectCampaignToggleLb(portalRedirectId: $portalRedirectId) }`, {portalRedirectId});
}

export function permaDeletePr(portalRedirectId: number) {
    return run(gql`mutation m($portalRedirectId: Int!) { portalRedirectCampaignPermaDelete(portalRedirectId: $portalRedirectId) }`, {portalRedirectId});
}

export function addPrComment(portalRedirectId: number, comment: string) {
    return run(gql`mutation m($portalRedirectId: Int!, $comment: String!) { portalRedirectCampaignAddComment(portalRedirectId: $portalRedirectId, comment: $comment) }`, {portalRedirectId, comment});
}

export function updatePrDupe(portalRedirectId: number, checker: any) {
    return run(gql`mutation m($portalRedirectId: Int!, $checker: PortalRedirectCheckerInput!) { portalRedirectCampaignUpdateDupe(portalRedirectId: $portalRedirectId, checker: $checker) }`, {portalRedirectId, checker});
}

export function updatePrScore(portalRedirectId: number, checker: any) {
    return run(gql`mutation m($portalRedirectId: Int!, $checker: PortalRedirectCheckerInput!) { portalRedirectCampaignUpdateScore(portalRedirectId: $portalRedirectId, checker: $checker) }`, {portalRedirectId, checker});
}

export async function deliverySandbox(post: any, values: {field: string; sample: string}[], execute: boolean) {
    const q = gql`
        mutation m($post: PortalRedirectCheckerInput!, $values: [PortalRedirectDeliveryValueInput!]!, $execute: Boolean!) {
            portalRedirectDeliverySandbox(post: $post, values: $values, execute: $execute) {
                request response resolution
            }
        }
    `;
    const res = await mutate(q, {post, values, execute});
    return res?.portalRedirectDeliverySandbox || null;
}

export async function deliveryResolutions(response: string, resolutions: any[]) {
    const q = gql`
        mutation m($response: String!, $resolutions: [PortalRedirectCheckerResolutionInput!]!) {
            portalRedirectDeliveryResolutions(response: $response, resolutions: $resolutions)
        }
    `;
    const res = await mutate(q, {response, resolutions});
    return res?.portalRedirectDeliveryResolutions || "";
}

export function downloadPrCampaignsCsv(rows: any[]) {
    const header = [
        "Company", "Broker", "School", "App Id", "SF Campaign Id", "LB Campaign Id", "LB Campaign Id Saved",
        "CPL SF", "CPL LB", "Active", "Grade SF", "Grade LB", "Branded", "Exclusive", "Exclusive Paused",
        "Exclusive Only", "Impressions", "Leads SF", "Leads LB", "Leads Total", "Revenue", "Conversion %",
        "EPI", "Daily Cap", "Monthly Cap", "Monthly SF", "Monthly LB", "Monthly Total", "Skip %", "Skip", "Landing Page",
    ];
    const body = rows.map((r) => [
        r.companyName,
        `${r.brokerName} (${r.brokerId})`,
        `${r.schoolName} (${r.schoolId})`,
        r.appId,
        r.hpCampaignId,
        r.hpCampaignIdLb || "",
        r.hpCampaignIdLbTemp || "",
        r.cplSf,
        r.hpCampaignIdLb ? r.cplLb : "",
        r.activeFlag ? "Yes" : "No",
        r.campaignGrade,
        r.campaignGradeLb,
        r.isBranded ? "Yes" : "No",
        r.isExclusive ? "Yes" : "No",
        r.isExclusiveTemp ? "Yes" : "No",
        r.isExclusiveOnly ? "Yes" : "No",
        r.impressions,
        r.leads,
        r.leadsLb,
        r.leadsTotal,
        r.revenue,
        r.ctr,
        r.cpi,
        r.dailyCap,
        r.monthlyCap,
        r.monthly,
        r.monthlyLb,
        r.monthlyTotal,
        r.skipPercent,
        r.skip,
        r.landingPage,
    ]);
    return downloadCsv("pr_campaigns.csv", [header, ...body]);
}
